import React, { Fragment, useEffect, useState } from "react";
import { Card, CardBody, Col, Container, Row, Badge, Button } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { API_ASSIST } from "../../../redux/apiRoutes";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import Breadcrumb from "../../common/breadcrumb/breadcrumb";
import { RefreshCw, Phone, User, Calendar, AlertTriangle } from "react-feather";

const StudentMonitoring = () => {
  const user = useSelector((content) => content.User);
  const navigate = useNavigate();
  
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const getStudentsWithConsecutiveAbsences = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_ASSIST}/monitoring/consecutive-absences`);
      if (response.data && response.data.students) {
        setStudents(response.data.students);
        setLastUpdated(new Date());
        toast.success(`Se encontraron ${response.data.totalStudents} estudiantes con faltas consecutivas`);
      } else {
        setStudents([]);
        toast.info("No se encontraron estudiantes con faltas consecutivas");
      }
    } catch (error) {
      console.error("Error fetching students with consecutive absences:", error);
      toast.error("Error al obtener la información de estudiantes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStudentsWithConsecutiveAbsences();
  }, []);

  const columns = [
    {
      name: "Estudiante",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center">
          <User size={16} className="me-2" />
          <div>
            <div className="fw-bold">{row.name}</div>
            <small className="text-muted">
              {row.grade} - {row.level}
            </small>
          </div>
        </div>
      ),
    },
    {
      name: "Faltas Consecutivas",
      selector: (row) => row.consecutiveAbsences,
      sortable: true,
      center: true,
      cell: (row) => (
        <Badge 
          color={row.consecutiveAbsences >= 5 ? "danger" : row.consecutiveAbsences >= 4 ? "warning" : "info"}
          className="fs-6"
        >
          {row.consecutiveAbsences}
        </Badge>
      ),
    },
    {
      name: "Total Faltas",
      selector: (row) => row.totalAbsences,
      sortable: true,
      center: true,
      cell: (row) => (
        <Badge color="secondary" className="fs-6">
          {row.totalAbsences}
        </Badge>
      ),
    },
    {
      name: "Última Falta",
      selector: (row) => row.lastAbsenceDate,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center">
          <Calendar size={14} className="me-1" />
          {row.lastAbsenceDate ? new Date(row.lastAbsenceDate).toLocaleDateString('es-ES') : 'N/A'}
        </div>
      ),
    },
    {
      name: "Teléfonos de Padres",
      selector: (row) => row.parentPhones,
      cell: (row) => (
        <div>
          {row.parentPhones && row.parentPhones.length > 0 ? (
            row.parentPhones.map((parent, index) => (
              <div key={index} className="mb-1">
                {parent.phone1 && (
                  <div className="d-flex align-items-center">
                    <Phone size={12} className="me-1" />
                    <a href={`tel:${parent.phone1}`} className="text-decoration-none">
                      {parent.phone1}
                    </a>
                  </div>
                )}
                {parent.phone2 && (
                  <div className="d-flex align-items-center">
                    <Phone size={12} className="me-1" />
                    <a href={`tel:${parent.phone2}`} className="text-decoration-none">
                      {parent.phone2}
                    </a>
                  </div>
                )}
              </div>
            ))
          ) : (
            <span className="text-muted">No disponible</span>
          )}
        </div>
      ),
    },
  ];

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #dee2e6",
      },
    },
    headCells: {
      style: {
        fontSize: "14px",
        fontWeight: "600",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
      },
    },
    cells: {
      style: {
        fontSize: "14px",
        padding: "12px 8px",
      },
    },
    rows: {
      style: {
        "&:nth-of-type(odd)": {
          backgroundColor: "#f8f9fa",
        },
        "&:hover": {
          backgroundColor: "#e9ecef",
        },
      },
    },
  };

  return (
    <Fragment>
      <Breadcrumb parent="Dashboard" title="Monitoreo de Estudiantes" />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h4 className="card-title mb-1">
                      <AlertTriangle className="me-2" size={20} />
                      Estudiantes con Faltas Consecutivas
                    </h4>
                    <p className="text-muted mb-0">
                      Monitoreo de estudiantes con 3 o más faltas consecutivas
                    </p>
                    {lastUpdated && (
                      <small className="text-muted">
                        Última actualización: {lastUpdated.toLocaleString('es-ES')}
                      </small>
                    )}
                  </div>
                  <Button
                    color="primary"
                    onClick={getStudentsWithConsecutiveAbsences}
                    disabled={loading}
                    className="d-flex align-items-center"
                  >
                    <RefreshCw 
                      size={16} 
                      className={`me-2 ${loading ? 'spin' : ''}`} 
                      style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }}
                    />
                    Actualizar
                  </Button>
                </div>

                <div className="row mb-3">
                  <div className="col-md-3">
                    <div className="card bg-primary text-white">
                      <div className="card-body">
                        <div className="d-flex align-items-center">
                          <div>
                            <h5 className="card-title mb-0">Total Estudiantes</h5>
                            <h3 className="mb-0">{students.length}</h3>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-warning text-white">
                      <div className="card-body">
                        <div className="d-flex align-items-center">
                          <div>
                            <h5 className="card-title mb-0">Con 3-4 Faltas</h5>
                            <h3 className="mb-0">
                              {students.filter(s => s.consecutiveAbsences >= 3 && s.consecutiveAbsences <= 4).length}
                            </h3>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-danger text-white">
                      <div className="card-body">
                        <div className="d-flex align-items-center">
                          <div>
                            <h5 className="card-title mb-0">Con 5+ Faltas</h5>
                            <h3 className="mb-0">
                              {students.filter(s => s.consecutiveAbsences >= 5).length}
                            </h3>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* <div className="col-md-3">
                    <div className="card bg-info text-white">
                      <div className="card-body">
                        <div className="d-flex align-items-center">
                          <div>
                            <h5 className="card-title mb-0">Promedio Faltas</h5>
                            <h3 className="mb-0">
                              {students.length > 0 ? 
                                (students.reduce((sum, s) => sum + s.consecutiveAbsences, 0) / students.length).toFixed(1) : 
                                '0'
                              }
                            </h3>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div> */}
                </div>

                <DataTable
                  columns={columns}
                  data={students}
                  pagination
                  paginationPerPage={10}
                  paginationRowsPerPageOptions={[10, 20, 50]}
                  noDataComponent={
                    <div className="text-center py-4">
                      <AlertTriangle size={48} className="text-muted mb-3" />
                      <h5>No hay estudiantes con faltas consecutivas</h5>
                      <p className="text-muted">
                        Todos los estudiantes están al día con sus asistencias
                      </p>
                    </div>
                  }
                  customStyles={customStyles}
                  progressPending={loading}
                  progressComponent={
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                      </div>
                      <p className="mt-2">Cargando información...</p>
                    </div>
                  }
                  highlightOnHover
                  pointerOnHover
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </Fragment>
  );
};

export default StudentMonitoring;
